import { Fragment, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ErrorState } from '@/shared/components/error-state';
import { CHART_GRID, chartSeries } from '@/shared/lib/chart-colors';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import { useChannelDailyReport } from '../api/reports.queries';
import { AXIS_PROPS } from '../lib/chart-axis';
import { formatPeriodKey } from '../lib/report-labels';
import type { ChannelDailyReport, ChannelDailyRow, ReportParams } from '../types/report.types';
import { ChartCard, ChartTooltipBox } from './chart-frame';
import { ReportNoData } from './report-no-data';

const COLLECTED = chartSeries(0);

/** One bar per day of the period: the chosen channel's collected, null on days it had none. */
interface DayPoint {
  day: string;
  collected: number | null;
  orders: number | null;
}

/**
 * Payment channels by day (reports.financial.read): what each channel brought
 * in each day — orders, collected, discounts, the expected channel fee and the
 * net the channel should transfer — with each day's subtotal, each channel's
 * total and the grand total per currency. Every figure is the API's.
 */
export function FinancialChannelsDaily({
  params,
  filtered,
  onClearFilters,
}: {
  params: ReportParams;
  filtered: boolean;
  onClearFilters: () => void;
}) {
  const report = useChannelDailyReport(params);

  if (report.isLoading) return <Skeleton className="h-64" />;
  if (report.error)
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;
  if (!report.data || report.data.rows.length === 0)
    return <ReportNoData filtered={filtered} onClear={onClearFilters} />;
  return <ChannelsDailyBody report={report.data} />;
}

function ChannelsDailyBody({ report }: { report: ChannelDailyReport }) {
  const currencies = report.totals.map((total) => total.currencyCode);
  const [chosenCurrency, setChosenCurrency] = useState<string | null>(null);
  const currency =
    chosenCurrency && currencies.includes(chosenCurrency)
      ? chosenCurrency
      : (currencies[0] ?? report.rows[0]?.currencyCode ?? 'USD');

  const rows = report.rows.filter((row) => row.currencyCode === currency);
  const channels = report.channelTotals.filter((row) => row.currencyCode === currency);
  const days = report.dayTotals.filter((row) => row.currencyCode === currency);
  const total = report.totals.find((row) => row.currencyCode === currency);

  return (
    <div className="space-y-4">
      <ChannelGrandTotals totals={report.totals} />

      {report.unpricedOrders > 0 && (
        <p className="text-status-warning text-xs" role="note">
          {formatNumber(report.unpricedOrders)} orders have no pricing record: listed as Unknown,
          fee not known.
        </p>
      )}

      {currencies.length > 1 && (
        <div className="flex justify-end" data-print="hide">
          <Select value={currency} onValueChange={setChosenCurrency}>
            <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((code) => (
                <SelectItem key={code} value={code} className="text-xs">
                  Amounts in {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <CollectedPerDay rows={rows} channels={channels} days={days} currency={currency} />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">By day and payment channel ({currency})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="text-xs" aria-label="Payment channels by day">
              <ChannelHeader first="Day" />
              <TableBody>
                {groupByDay(rows).map(([day, dayRows]) => {
                  const subtotal = days.find((row) => row.day === day);
                  return (
                    <Fragment key={day}>
                      {dayRows.map((row, index) => (
                        <TableRow key={`${day}-${row.channelCode ?? ''}`}>
                          <TableCell className="whitespace-nowrap">
                            {index === 0 ? formatPeriodKey(day, true) : null}
                          </TableCell>
                          <TableCell>
                            <ChannelName row={row} />
                          </TableCell>
                          <MoneyCells row={row} />
                        </TableRow>
                      ))}
                      {subtotal && dayRows.length > 1 && (
                        <TableRow className="bg-muted/40 font-medium">
                          <TableCell />
                          <TableCell>Day total</TableCell>
                          <MoneyCells row={subtotal} />
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
              {total && (
                <TableFooter>
                  <TableRow>
                    <TableCell>Total</TableCell>
                    <TableCell>{currency}</TableCell>
                    <MoneyCells row={total} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Per payment channel ({currency})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="text-xs" aria-label="Per payment channel">
              <ChannelHeader first="Channel" withoutSecond />
              <TableBody>
                {channels.map((row) => (
                  <TableRow key={row.channelCode ?? 'none'}>
                    <TableCell>
                      <ChannelName row={row} />
                    </TableCell>
                    <MoneyCells row={row} />
                  </TableRow>
                ))}
              </TableBody>
              {total && (
                <TableFooter>
                  <TableRow>
                    <TableCell>Total</TableCell>
                    <MoneyCells row={total} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** The rows of one currency, in the API's order, gathered under their day. */
function groupByDay(rows: readonly ChannelDailyRow[]): [string, ChannelDailyRow[]][] {
  const days = new Map<string, ChannelDailyRow[]>();
  for (const row of rows) {
    const day = row.day ?? '';
    days.set(day, [...(days.get(day) ?? []), row]);
  }
  return [...days.entries()];
}

function ChannelHeader({ first, withoutSecond }: { first: string; withoutSecond?: boolean }) {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>{first}</TableHead>
        {!withoutSecond && <TableHead>Channel</TableHead>}
        <TableHead className="text-end">Orders</TableHead>
        <TableHead className="text-end">Collected</TableHead>
        <TableHead className="text-end">Discounts</TableHead>
        <TableHead className="text-end">Channel fees</TableHead>
        <TableHead className="text-end">Net expected</TableHead>
        <TableHead className="text-end">Avg order</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function ChannelName({ row }: { row: ChannelDailyRow }) {
  if (!row.channelCode) return <span className="text-muted-foreground">Unknown</span>;
  return (
    <div className="min-w-0">
      <span>{row.channelName ?? row.channelCode}</span>
      <span className="text-muted-foreground block font-mono text-[10px]">{row.channelCode}</span>
    </div>
  );
}

function MoneyCells({ row }: { row: ChannelDailyRow }) {
  const money = (value: number) => formatCurrency(value, row.currencyCode);
  return (
    <>
      <TableCell className="text-end tabular-nums">{formatNumber(row.orders)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.collected)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.discounts)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.channelFees)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.netExpected)}</TableCell>
      <TableCell className="text-end tabular-nums">
        {row.averageOrder === null ? '—' : money(row.averageOrder)}
      </TableCell>
    </>
  );
}

/** The grand total per currency: amounts in different currencies are never added up. */
function ChannelGrandTotals({ totals }: { totals: readonly ChannelDailyRow[] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Totals per currency">
      {totals.map((total) => (
        <li key={total.currencyCode}>
          <Card className="gap-1 py-3">
            <CardContent className="space-y-1 px-4">
              <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {total.currencyCode} · {formatNumber(total.orders)} orders
              </p>
              <p className="font-display text-xl font-bold tabular-nums">
                {formatCurrency(total.collected, total.currencyCode)}
                <span className="text-muted-foreground ms-1.5 text-xs font-normal">collected</span>
              </p>
              <p className="text-muted-foreground text-xs">
                Channel fees {formatCurrency(total.channelFees, total.currencyCode)} · net expected{' '}
                <span className="text-foreground font-medium">
                  {formatCurrency(total.netExpected, total.currencyCode)}
                </span>
              </p>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}

/**
 * Collected per day on ONE channel (one series — the brand palette tells two
 * apart at most, and a channel picker keeps it readable with many channels).
 */
function CollectedPerDay({
  rows,
  channels,
  days,
  currency,
}: {
  rows: readonly ChannelDailyRow[];
  channels: readonly ChannelDailyRow[];
  days: readonly ChannelDailyRow[];
  currency: string;
}) {
  const codes = channels.flatMap((row) => (row.channelCode ? [row.channelCode] : []));
  const [chosen, setChosen] = useState<string | null>(null);
  // The API lists channels largest first: the default is the biggest one.
  const channel = chosen && codes.includes(chosen) ? chosen : (codes[0] ?? null);
  const name = channels.find((row) => row.channelCode === channel)?.channelName ?? channel;
  const points: DayPoint[] = days.flatMap((total) => {
    if (!total.day) return [];
    const row = rows.find((item) => item.day === total.day && item.channelCode === channel);
    return [{ day: total.day, collected: row?.collected ?? null, orders: row?.orders ?? null }];
  });

  return (
    <div className="space-y-2">
      {codes.length > 1 && channel && (
        <div className="flex justify-end" data-print="hide">
          <Select value={channel} onValueChange={setChosen}>
            <SelectTrigger size="sm" className="h-8 w-52 text-xs" aria-label="Chart channel">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {channels.map((row) =>
                row.channelCode ? (
                  <SelectItem key={row.channelCode} value={row.channelCode} className="text-xs">
                    {row.channelName ?? row.channelCode}
                  </SelectItem>
                ) : null,
              )}
            </SelectContent>
          </Select>
        </div>
      )}
      <ChartCard
        title={`Collected per day — ${name ?? 'no channel'} (${currency})`}
        isEmpty={points.every((point) => point.collected === null)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="day"
              tickFormatter={(day: string) => formatPeriodKey(day)}
              minTickGap={24}
              {...AXIS_PROPS}
            />
            <YAxis
              width={64}
              tickFormatter={(value: number) => formatNumber(value)}
              {...AXIS_PROPS}
            />
            <Tooltip
              cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
              content={({ active, payload }) => {
                const point = payload?.[0]?.payload as DayPoint | undefined;
                if (!active || !point) return null;
                return (
                  <ChartTooltipBox
                    title={formatPeriodKey(point.day, true)}
                    lines={[
                      {
                        label: 'collected',
                        value:
                          point.collected === null
                            ? '—'
                            : formatCurrency(point.collected, currency),
                        color: COLLECTED,
                      },
                      { label: 'orders', value: formatNumber(point.orders ?? 0) },
                    ]}
                  />
                );
              }}
            />
            <Bar
              dataKey="collected"
              fill={COLLECTED}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
